import moment, { Moment } from 'moment';
import React, { useEffect, useMemo, useState } from 'react';

import { States } from '@vestahealthcare/common/enums';
import { SelectableInfo } from '@vestahealthcare/common/enums/Enum';
import { translate } from '@vestahealthcare/common/i18n';
import { Employee, PodConfig } from '@vestahealthcare/common/models';
import { DATE_FORMAT } from '@vestahealthcare/common/utils/constants';

import { FilterSelectable, Modal, PanelInfo, Select } from 'styleguide-v2';
import { FilterEmployeeOptions } from 'styleguide-v2/src/components/SelectAssignee';

import { CacheServices } from 'dash/src/services';

import { showGlobalError } from '../../../../../components/GlobalMessage';
import {
  BULK_EDIT_ERROR_NP_STATE,
  BULK_EDIT_ERROR_POD,
  BULK_EDIT_ERROR_RN_STATE,
} from '../../../../../services/PatientServices';
import {
  checkPODShuffle,
  checkPODShuffleForecast,
  editPODShuffleAsync,
  schedulePODShuffleAsync,
} from '../../../../../services/PodServices';
import { PODShuffleResultsFilters } from './PODShuffleResultsFilterBar';

interface Props {
  onClose: () => void;
  onSubmit: () => void;
  open: boolean;
  selection: number[];
  isEdit?: boolean;
  filters?: PODShuffleResultsFilters;
  allSelected: boolean;
}

export const EditPodModal = ({
  onClose,
  onSubmit,
  open,
  selection,
  isEdit,
  filters,
  allSelected,
}: Props) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingData, setLoadingData] = useState<boolean>(false);
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [step, setStep] = useState<number>(0);

  const [selectedPod, setSelectedPod] = useState<number>();
  const [selectedPodManager, setSelectedPodManager] = useState<number>();
  const [selectedNP, setSelectedNP] = useState<number>();
  const [selectedRN, setSelectedRN] = useState<number>();
  const [selectedHealthCoaches, setSelectedHealthCoaches] = useState<
    number[]
  >();
  const [selectedCareCoordinators, setSelectedCareCoordinators] = useState<
    number[]
  >();
  const [selectedEngagementOwners, setSelectedEngagementOwners] = useState<
    number[]
  >();
  const [selectedMonthYear, setSelectedMonthYear] = useState<string>();

  const [podConfigs, setPodConfigs] = useState<SelectableInfo<PodConfig>[]>([]);

  const [npErrorStates, setNPErrorStates] = useState<States[]>([]);
  const [rnErrorStates, setRNErrorStates] = useState<States[]>([]);
  const [podErrorEmployees, setPODErrorEmployees] = useState<string[]>([]);
  const [hasErrors, setHasErrors] = useState<boolean>(false);

  const [podChange, setPodChange] = useState<boolean>(false);
  const [checkingPodChange, setCheckingPodChange] = useState<boolean>(false);

  const selectedPodConfig = useMemo(
    () => podConfigs?.find(({ value }) => selectedPod === value),
    [selectedPod, podConfigs],
  );

  const podStaff = useMemo(() => {
    if (!selectedPodConfig?.info)
      return {
        podManagers: [],
        nps: [],
        rns: [],
        healthCoaches: [],
        clinicalAssistants: [],
        engagementOwners: [],
      };

    const config = selectedPodConfig.info;

    return {
      podManagers: config.podManager ? [config.podManager] : [],
      nps:
        config.assignedNPs
          ?.filter((slot) => slot.employee.enabled)
          ?.map((slot) => slot.employee) || [],
      rns:
        config.assignedRNs
          ?.filter((slot) => slot.employee.enabled)
          ?.map((slot) => slot.employee) || [],
      healthCoaches:
        config.assignedHealthCoaches
          ?.filter((slot) => slot.employee.enabled)
          ?.map((slot) => slot.employee) || [],
      clinicalAssistants:
        config.assignedCareCoordinators
          ?.filter((slot) => slot.employee.enabled)
          ?.map((slot) => slot.employee) || [],
      engagementOwners:
        config.assignedEngagementOwners
          ?.filter((slot) => slot.employee.enabled)
          ?.map((slot) => slot.employee) || [],
    };
  }, [selectedPodConfig]);

  const NUMBER_OF_FUTURE_MONTHS = 3;

  const monthYearOptions = useMemo(() => {
    return Array.from({ length: NUMBER_OF_FUTURE_MONTHS }).map((_, i) =>
      moment()
        .add(i + 1, 'month')
        .startOf('month'),
    );
  }, []);

  const isFirstMonthDisabled = useMemo(() => {
    const today = new Date();
    const currentETTime = new Date(
      today.toLocaleString('en-US', { timeZone: 'America/New_York' }),
    );
    const currentETHour = currentETTime.getHours();
    const currentETDate = currentETTime.getDate();
    const lastDayOfMonth = new Date(
      currentETTime.getFullYear(),
      currentETTime.getMonth() + 1,
      0,
    ).getDate();
    return currentETDate === lastDayOfMonth && currentETHour >= 22;
  }, []);

  const fetchInitialData = async () => {
    setLoadingData(true);
    try {
      const configs = await CacheServices.getPodConfigs();

      const activePods = configs.filter((pod) => pod.active);
      setPodConfigs(PodConfig.toSelectable(activePods));
    } finally {
      setLoadingData(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchInitialData();
      resetModal();
    }
  }, [open]);

  useEffect(() => {
    if (!selectedPodConfig) return;

    if (podStaff.podManagers.length === 1) {
      setSelectedPodManager(podStaff.podManagers[0].id);
    }
    if (podStaff.nps.length === 1) {
      setSelectedNP(podStaff.nps[0].id);
    }
    if (podStaff.rns.length === 1) {
      setSelectedRN(podStaff.rns[0].id);
    }
    if (podStaff.healthCoaches.length === 1) {
      setSelectedHealthCoaches(podStaff.healthCoaches.map((h) => h.id));
    }
    if (podStaff.clinicalAssistants.length === 1) {
      setSelectedCareCoordinators(podStaff.clinicalAssistants.map((c) => c.id));
    }
    if (podStaff.engagementOwners.length === 1) {
      setSelectedEngagementOwners(podStaff.engagementOwners.map((e) => e.id));
    }
  }, [podStaff, selectedPodConfig]);

  const resetModal = () => {
    setSubmitted(false);
    setStep(0);
    setSelectedPod(undefined);
    setSelectedPodManager(undefined);
    setSelectedNP(undefined);
    setSelectedRN(undefined);
    setSelectedHealthCoaches(undefined);
    setSelectedCareCoordinators(undefined);
    setSelectedEngagementOwners(undefined);
    setSelectedMonthYear(undefined);
    setNPErrorStates([]);
    setRNErrorStates([]);
    setPODErrorEmployees([]);
    setHasErrors(false);
    setPodChange(false);
    setCheckingPodChange(false);
  };

  const isStep1Valid = () => {
    if (!selectedPod) return false;

    if (podChange) {
      const podManagerRequired =
        podStaff.podManagers.length > 1 && !selectedPodManager;
      const npRequired = podStaff.nps.length > 1 && !selectedNP;
      const rnRequired = podStaff.rns.length > 1 && !selectedRN;
      const healthCoachRequired =
        podStaff.healthCoaches.length > 1 &&
        (!selectedHealthCoaches || selectedHealthCoaches.length === 0);
      const careCoordinatorRequired =
        podStaff.clinicalAssistants.length > 1 &&
        (!selectedCareCoordinators || selectedCareCoordinators.length === 0);

      return !(
        podManagerRequired ||
        npRequired ||
        rnRequired ||
        healthCoachRequired ||
        careCoordinatorRequired
      );
    }

    return true;
  };

  const isStep2Valid = () => {
    return !!selectedMonthYear;
  };

  const handleStep1Submit = async () => {
    setSubmitted(true);

    if (!isStep1Valid()) {
      return;
    }

    if (isEdit) {
      await handleFinalSubmit();
      return;
    }

    setStep(1);
    setSubmitted(false);
  };

  const handleFinalSubmit = async () => {
    setSubmitted(true);

    if (!isStep2Valid() && !isEdit) {
      return;
    }

    if (isEdit ? !selectedPod : !selectedPod || !selectedMonthYear) {
      return;
    }
    setLoading(true);

    const response = await (isEdit
      ? editPODShuffleAsync
      : schedulePODShuffleAsync)(allSelected && isEdit ? [] : selection, {
      podId: selectedPod,
      month: isEdit ? undefined : selectedMonthYear,
      podManagerId: selectedPodManager,
      npOwnerId: selectedNP,
      rnOwnerId: selectedRN,
      healthCoachIds: selectedHealthCoaches,
      careCoordinatorIds: selectedCareCoordinators,
      engagementOwnerIds: selectedEngagementOwners,
      podChange,
      ...(isEdit && allSelected
        ? {
            filter: {
              month: moment(
                (filters?.month?.value as FilterSelectable)?.value,
              ).format(DATE_FORMAT),
              podId: (filters?.podId?.value as FilterSelectable)?.value,
              excludedPodShuffleMemberIds: selection,
            },
          }
        : {}),
    }).catch((e) => {
      showGlobalError(e as string);
      setLoading(false);
      return null;
    });

    setLoading(false);

    if (!response) {
      return;
    }

    if (response?.errors?.length) {
      setNPErrorStates(
        (response.errors?.find(({ type }) => type === BULK_EDIT_ERROR_NP_STATE)
          ?.extraData || []) as States[],
      );
      setRNErrorStates(
        (response.errors?.find(({ type }) => type === BULK_EDIT_ERROR_RN_STATE)
          ?.extraData || []) as States[],
      );
      setPODErrorEmployees(
        (response.errors?.find(({ type }) => type === BULK_EDIT_ERROR_POD)
          ?.extraData || []) as string[],
      );
      setHasErrors(true);
      return false;
    }

    onSubmit();
    onClose();
  };

  const clearErrors = () => {
    setNPErrorStates([]);
    setRNErrorStates([]);
    setPODErrorEmployees([]);
    setHasErrors(false);
    setSubmitted(false);
  };

  const handleBack = () => {
    setStep(0);
    clearErrors();
  };

  const getSubmitText = () => {
    if (isEdit) return translate('pods.shuffle.schedule.editPODShuffle');
    return step === 0
      ? translate('pods.shuffle.schedule.submit')
      : translate('pods.shuffle.schedule.save');
  };

  const handleSubmit = async () => {
    if (step === 0) {
      await handleStep1Submit();
    } else {
      await handleFinalSubmit();
    }
    return false;
  };

  const renderErrors = () => {
    return (
      <>
        {!!podErrorEmployees?.length && (
          <PanelInfo
            className="grid-span-12"
            title={
              <div>
                <p style={{ color: 'inherit' }}>
                  {translate('memberBulkEdit.errorPodAssignment', {
                    podName: selectedPodConfig?.label || 'POD',
                  })}
                </p>

                <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                  {podErrorEmployees.map((employee) => (
                    <li
                      key={`error-pod-${employee}`}
                      style={{ marginBottom: '4px' }}
                    >
                      {translate('memberBulkEdit.errorPodAssignmentEmployee', {
                        name: employee,
                      })}
                    </li>
                  ))}
                </ul>
                <p style={{ color: 'inherit' }}>
                  {translate('memberBulkEdit.errorPodAssignmentSubtitle')}
                </p>
              </div>
            }
            type="error"
          />
        )}

        {!!(npErrorStates?.length || rnErrorStates?.length) && (
          <PanelInfo
            className="grid-span-12"
            title={
              <div>
                <p style={{ color: 'inherit' }}>
                  {translate('memberBulkEdit.errorStateAssignment')}
                </p>

                <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                  {selectedNP &&
                    !!npErrorStates?.length &&
                    npErrorStates.map((state) => (
                      <li
                        key={`error-state-np-${state.value}`}
                        style={{ marginBottom: '4px' }}
                      >
                        {translate(
                          'memberBulkEdit.errorStateAssignmentNotLicensed',
                          {
                            name: Employee.toSelectable(podStaff.nps).find(
                              ({ value }) => value === selectedNP,
                            )?.label,
                            state: state.toString(),
                          },
                        )}
                      </li>
                    ))}
                  {selectedRN &&
                    selectedRN !== selectedNP &&
                    !!rnErrorStates?.length &&
                    rnErrorStates.map((state) => (
                      <li
                        key={`error-state-rn-${state.value}`}
                        style={{ marginBottom: '4px' }}
                      >
                        {translate(
                          'memberBulkEdit.errorStateAssignmentNotLicensed',
                          {
                            name: Employee.toSelectable(podStaff.rns).find(
                              ({ value }) => value === selectedRN,
                            )?.label,
                            state: state.toString(),
                          },
                        )}
                      </li>
                    ))}
                </ul>
              </div>
            }
            type="error"
          />
        )}
      </>
    );
  };

  return (
    <Modal
      maxWidth="md"
      title={translate('pods.shuffle.schedule.destinationPod')}
      onClose={onClose}
      onSubmit={handleSubmit}
      open={open}
      submitText={getSubmitText()}
      submitDisabled={loading || (step === 1 && hasErrors)}
      cancelText={
        step === 0
          ? translate('pods.shuffle.schedule.cancel')
          : translate('pods.shuffle.schedule.goBack')
      }
      onCancel={step === 0 ? onClose : handleBack}
      stepSelected={step}
      hideStepsNavigation
      steps={[
        {
          label: '',
          detail: (
            <div className="grid-wrapper fit">
              <Select
                className="grid-span-12"
                error={submitted && !selectedPod}
                label={translate('pods.shuffle.schedule.pod')}
                placeholder={translate('pods.shuffle.schedule.selectPod')}
                items={podConfigs || []}
                value={podConfigs?.find(({ value }) => value === selectedPod)}
                onChange={async (pod: SelectableInfo<PodConfig>) => {
                  if (!pod?.value) {
                    setSelectedPod(undefined);
                    setPodChange(false);
                    return;
                  }

                  setCheckingPodChange(true);
                  try {
                    const { podChange } = await (isEdit
                      ? checkPODShuffleForecast
                      : checkPODShuffle)({
                      ...(isEdit
                        ? {
                            podShuffleMemberIds: !allSelected
                              ? selection
                              : undefined,
                            podId: pod?.value as number,
                            ...(allSelected
                              ? {
                                  filter: {
                                    month: moment(
                                      (filters?.month
                                        ?.value as FilterSelectable)?.value,
                                    ).format(DATE_FORMAT),
                                    podId: (filters?.podId
                                      ?.value as FilterSelectable)?.value,
                                    excludedPodShuffleMemberIds: selection,
                                  },
                                }
                              : {}),
                          }
                        : {
                            memberIds: selection,
                            podId: pod?.value as number,
                          }),
                    });

                    setPodChange(podChange || false);
                    setSelectedPodManager(undefined);
                    setSelectedNP(undefined);
                    setSelectedRN(undefined);
                    setSelectedHealthCoaches(undefined);
                    setSelectedCareCoordinators(undefined);
                    setSelectedEngagementOwners(undefined);
                    setSelectedPod(pod?.value as number);
                    clearErrors();
                  } catch (error) {
                    showGlobalError(error as string);
                  } finally {
                    setCheckingPodChange(false);
                  }
                }}
                loading={loadingData || checkingPodChange}
                required
              />

              {selectedPod && !checkingPodChange && (
                <>
                  <Select
                    className="grid-span-6"
                    filterOptions={FilterEmployeeOptions}
                    label={translate('pods.shuffle.schedule.podManager')}
                    placeholder={translate(
                      'pods.shuffle.schedule.selectPodManager',
                    )}
                    items={Employee.toSelectable(podStaff.podManagers)}
                    value={Employee.toSelectable(podStaff.podManagers).find(
                      ({ value }) => value === selectedPodManager,
                    )}
                    onChange={(emp: SelectableInfo<Employee>) => {
                      clearErrors();
                      setSelectedPodManager(emp?.value as number);
                    }}
                    disabled={
                      !podStaff.podManagers.length ||
                      podStaff.podManagers.length === 1
                    }
                    error={
                      submitted &&
                      podChange &&
                      podStaff.podManagers.length > 1 &&
                      !selectedPodManager
                    }
                    required={podChange && podStaff.podManagers.length > 1}
                  />

                  <Select
                    className="grid-span-6"
                    filterOptions={FilterEmployeeOptions}
                    label={translate('pods.shuffle.schedule.npOwner')}
                    placeholder={translate('pods.shuffle.schedule.selectNP')}
                    items={Employee.toSelectable(podStaff.nps)}
                    value={Employee.toSelectable(podStaff.nps).find(
                      ({ value }) => value === selectedNP,
                    )}
                    onChange={(emp: SelectableInfo<Employee>) => {
                      clearErrors();
                      setSelectedNP(emp?.value as number);
                    }}
                    disabled={!podStaff.nps.length || podStaff.nps.length === 1}
                    error={
                      submitted &&
                      podChange &&
                      podStaff.nps.length > 1 &&
                      !selectedNP
                    }
                    required={podChange && podStaff.nps.length > 1}
                  />

                  <Select
                    className="grid-span-6"
                    filterOptions={FilterEmployeeOptions}
                    label={translate('pods.shuffle.schedule.rnOwner')}
                    placeholder={translate('pods.shuffle.schedule.selectRN')}
                    items={Employee.toSelectable(podStaff.rns)}
                    value={Employee.toSelectable(podStaff.rns).find(
                      ({ value }) => value === selectedRN,
                    )}
                    onChange={(emp: SelectableInfo<Employee>) => {
                      clearErrors();

                      setSelectedRN(emp?.value as number);
                    }}
                    disabled={!podStaff.rns.length || podStaff.rns.length === 1}
                    error={
                      submitted &&
                      podChange &&
                      podStaff.rns.length > 1 &&
                      !selectedRN
                    }
                    required={podChange && podStaff.rns.length > 1}
                  />

                  <Select
                    className="grid-span-6"
                    filterOptions={FilterEmployeeOptions}
                    label={translate('pods.shuffle.schedule.healthCoachOwner')}
                    placeholder={translate(
                      'pods.shuffle.schedule.selectHealthCoach',
                    )}
                    items={Employee.toSelectable(podStaff.healthCoaches)}
                    value={
                      selectedHealthCoaches
                        ? Employee.toSelectable(
                            podStaff.healthCoaches.filter((h) =>
                              selectedHealthCoaches.includes(h.id),
                            ),
                          )
                        : undefined
                    }
                    onChange={(emp: SelectableInfo<Employee>[]) => {
                      clearErrors();

                      setSelectedHealthCoaches(emp?.map((e) => +e.value) || []);
                    }}
                    multiple
                    disabled={
                      !podStaff.healthCoaches.length ||
                      podStaff.healthCoaches.length === 1
                    }
                    error={
                      submitted &&
                      podChange &&
                      podStaff.healthCoaches.length > 1 &&
                      (!selectedHealthCoaches ||
                        selectedHealthCoaches.length === 0)
                    }
                    required={podChange && podStaff.healthCoaches.length > 1}
                  />

                  <Select
                    className="grid-span-6"
                    filterOptions={FilterEmployeeOptions}
                    label={translate(
                      'pods.shuffle.schedule.clinicalAssistantOwner',
                    )}
                    multiple
                    placeholder={translate(
                      'pods.shuffle.schedule.selectClinicalAssistant',
                    )}
                    items={Employee.toSelectable(podStaff.clinicalAssistants)}
                    value={
                      selectedCareCoordinators
                        ? Employee.toSelectable(
                            podStaff.clinicalAssistants.filter((c) =>
                              selectedCareCoordinators.includes(c.id),
                            ),
                          )
                        : undefined
                    }
                    onChange={(emp: SelectableInfo<Employee>[]) => {
                      clearErrors();

                      setSelectedCareCoordinators(
                        emp?.map((e) => +e.value) || [],
                      );
                    }}
                    disabled={
                      !podStaff.clinicalAssistants.length ||
                      podStaff.clinicalAssistants.length === 1
                    }
                    error={
                      submitted &&
                      podChange &&
                      podStaff.clinicalAssistants.length > 1 &&
                      (!selectedCareCoordinators ||
                        selectedCareCoordinators.length === 0)
                    }
                    required={
                      podChange && podStaff.clinicalAssistants.length > 1
                    }
                  />

                  <Select
                    className="grid-span-6"
                    filterOptions={FilterEmployeeOptions}
                    label={translate('pods.shuffle.schedule.engagementOwner')}
                    placeholder={translate(
                      'pods.shuffle.schedule.selectEngagementOwner',
                    )}
                    items={Employee.toSelectable(podStaff.engagementOwners)}
                    value={
                      selectedEngagementOwners
                        ? Employee.toSelectable(
                            podStaff.engagementOwners.filter((e) =>
                              selectedEngagementOwners.includes(e.id),
                            ),
                          )
                        : undefined
                    }
                    onChange={(emp: SelectableInfo<Employee>[]) => {
                      clearErrors();

                      setSelectedEngagementOwners(
                        emp?.map((e) => +e.value) || [],
                      );
                    }}
                    multiple
                    disabled={
                      !podStaff.engagementOwners.length ||
                      podStaff.engagementOwners.length === 1
                    }
                  />
                </>
              )}
              {isEdit && renderErrors()}
            </div>
          ),
        },
        {
          label: '',
          detail: (
            <div className="grid-wrapper fit">
              {renderErrors()}
              <Select
                className="grid-span-12"
                error={submitted && !selectedMonthYear}
                label={translate(
                  'pods.shuffle.schedule.whenShouldTheShuffleHappen',
                )}
                placeholder={translate(
                  'pods.shuffle.schedule.selectMonthAndYear',
                )}
                items={monthYearOptions}
                getItemLabel={(date: Moment) =>
                  date.format('MMM YYYY').toLocaleUpperCase()
                }
                getItemDisabled={(date: Moment) =>
                  monthYearOptions[0] === date && isFirstMonthDisabled
                }
                value={
                  selectedMonthYear
                    ? monthYearOptions.find(
                        (date) =>
                          date.format(DATE_FORMAT) === selectedMonthYear,
                      )
                    : undefined
                }
                onChange={(date: Moment) => {
                  setSelectedMonthYear(
                    date ? date.format(DATE_FORMAT) : undefined,
                  );
                }}
                required
              />
            </div>
          ),
        },
      ]}
    />
  );
};
